/**
 * React Query hooks for Member Role operations
 */

import { useQuery, useMutation, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import memberRoleService, {
  type AvailableMembersPage,
  type MemberRoleListResponse,
} from '../services/member-role.service';
import type {
  ATSMemberRole,
  ATSMemberRoleCreate,
  ATSMemberRoleUpdate,
} from '../types/member-role';

interface UseMemberRoleListQueryOptions {
  workspaceId?: string;
  idToken?: string;
  enabled?: boolean;
  refetchInterval?: number | false;
}

interface UseAvailableMembersQueryOptions {
  workspaceId?: string;
  idToken?: string;
  search?: string;
  skip?: number;
  limit?: number;
  enabled?: boolean;
}

/**
 * Hook to query available members (with ATS seats but no roles).
 * Supports search and pagination.
 */
export function useAvailableMembersQuery({
  workspaceId,
  idToken,
  search = '',
  skip = 0,
  limit = 20,
  enabled = true,
}: UseAvailableMembersQueryOptions): UseQueryResult<AvailableMembersPage, Error> {
  return useQuery<AvailableMembersPage, Error>({
    queryKey: ['available-members', workspaceId, search, skip, limit],
    queryFn: async () => {
      if (!workspaceId || !idToken) {
        throw new Error('Workspace ID and ID token are required');
      }
      return memberRoleService.getAvailableMembers(workspaceId, idToken, { search, skip, limit });
    },
    enabled: enabled && !!workspaceId && !!idToken,
    staleTime: 30000,
    placeholderData: (prev) => prev,
  });
}

/**
 * Hook to query list of member roles
 */
export function useMemberRoleListQuery({
  workspaceId,
  idToken,
  enabled = true,
  refetchInterval,
}: UseMemberRoleListQueryOptions): UseQueryResult<MemberRoleListResponse, Error> {
  return useQuery<MemberRoleListResponse, Error>({
    queryKey: ['member-roles', workspaceId],
    queryFn: async () => {
      if (!workspaceId || !idToken) {
        throw new Error('Workspace ID and ID token are required');
      }
      return memberRoleService.listMemberRoles(workspaceId, idToken);
    },
    enabled: enabled && !!workspaceId && !!idToken,
    staleTime: 30000, // Cache for 30 seconds
    refetchInterval,
  });
}

interface UseRecruiterListQueryOptions {
  workspaceId?: string;
  idToken?: string;
  search?: string;
  skip?: number;
  limit?: number;
  enabled?: boolean;
}

export function useRecruiterListQuery({
  workspaceId,
  idToken,
  search = '',
  skip = 0,
  limit = 10,
  enabled = true,
}: UseRecruiterListQueryOptions) {
  return useQuery({
    queryKey: ['recruiters', workspaceId, search, skip, limit],
    queryFn: async () => {
      if (!workspaceId || !idToken) {
        throw new Error('Workspace ID and ID token are required');
      }
      return memberRoleService.listRecruiters(workspaceId, idToken, { search, skip, limit });
    },
    enabled: enabled && !!workspaceId && !!idToken,
    staleTime: 30000,
    placeholderData: (prev) => prev,
  });
}

interface UseAssignMemberRoleMutationOptions {
  workspaceId: string;
  idToken: string;
  onSuccess?: (data: ATSMemberRole) => void;
  onError?: (error: Error) => void;
}

/**
 * Hook to assign a member role
 */
export function useAssignMemberRoleMutation({
  workspaceId,
  idToken,
  onSuccess,
  onError,
}: UseAssignMemberRoleMutationOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ATSMemberRoleCreate) =>
      memberRoleService.assignMemberRole(data, workspaceId, idToken),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['member-roles', workspaceId] });
      queryClient.invalidateQueries({ queryKey: ['available-members', workspaceId] });
      onSuccess?.(data);
    },
    onError,
  });
}

interface UseBulkAssignMemberRoleMutationOptions {
  workspaceId: string;
  idToken: string;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Hook to bulk assign member roles in one request
 */
export function useBulkAssignMemberRoleMutation({
  workspaceId,
  idToken,
  onSuccess,
  onError,
}: UseBulkAssignMemberRoleMutationOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (assignments: Array<{ account_id: string; role: string }>) =>
      memberRoleService.bulkAssignMemberRoles(assignments, workspaceId, idToken),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['member-roles', workspaceId] });
      queryClient.invalidateQueries({ queryKey: ['available-members', workspaceId] });
      onSuccess?.();
    },
    onError,
  });
}

interface UseUpdateMemberRoleMutationOptions {
  workspaceId: string;
  idToken: string;
  onSuccess?: (data: ATSMemberRole) => void;
  onError?: (error: Error) => void;
}

/**
 * Hook to update a member role
 */
export function useUpdateMemberRoleMutation({
  workspaceId,
  idToken,
  onSuccess,
  onError,
}: UseUpdateMemberRoleMutationOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ accountId, data }: { accountId: string; data: ATSMemberRoleUpdate }) =>
      memberRoleService.updateMemberRole(accountId, data, workspaceId, idToken),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['member-roles', workspaceId] });
      onSuccess?.(data);
    },
    onError,
  });
}

interface UseRemoveMemberRoleMutationOptions {
  workspaceId: string;
  idToken: string;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Hook to remove a member role
 */
export function useRemoveMemberRoleMutation({
  workspaceId,
  idToken,
  onSuccess,
  onError,
}: UseRemoveMemberRoleMutationOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (accountId: string) =>
      memberRoleService.removeMemberRole(accountId, workspaceId, idToken),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['member-roles', workspaceId] });
      queryClient.invalidateQueries({ queryKey: ['available-members', workspaceId] });
      onSuccess?.();
    },
    onError,
  });
}
